<template>
  <div class="flatfinder">
    <Leaflet
      ref="leaflet"
      :value="value"
      :image="image"
      :getStyle="getStyle"
      :getTitle="getTitle"
      :actions="actions"
      geometry-path="data"
      id-resolver="reference.ref.id"
      @add="add"
      @click:layer="openActions"
      @input="$ev => $emit('input', $ev)"
    />
    <transition name="actions">
      <div v-if="layer.handler" class="flatfinder__actions">
        <ButtonComponent :label="$t('cancel')" theme="" @click="cancelEdit" />
        <ButtonComponent :label="$t('confirm')" theme="primary" @click="confirmEdit" />
      </div>
    </transition>

    <SettingsDropdown
      v-if="dropdownIsOpen"
      :coords="mouse"
      @close="dropdownIsOpen = false"
      @settings="openSettings"
      @edit="editLayer"
      @delete="deleteLayer"
    />
    <SettingsModal
      :show.sync="settingsIsOpen"
      :selected="selected"
      :resources="{ fetch: getResources, isDisabled: resourceIsDisabled }"
      @submit="onSettingsSubmit"
    />
    <ResourceSelect
      ref="selector"
      :value="selected"
      :items="getResources"
      :is-disabled="resourceIsDisabled"
      search
      type="modal-only"
    />
  </div>
</template>

<script>
import Leaflet from '@/components/Leaflet'
import FlatfinderApi from '@/api/Flatfinder'
import { getTitle } from '@/components/Flatfinder/Resources/Card'
import ResourceSelect from '@/components/Flatfinder/Resources/Select/Modal'
import SettingsDropdown from '@/components/Flatfinder/Settings/Dropdown'
import SettingsModal from '@/components/Flatfinder/Settings/Modal'
import { getStatusClass, Nest } from '@/components/Flatfinder/utils'
import { ButtonComponent } from 'vue-elder-button'
import { PartialUpdate } from '@/utils'
import { mapState } from 'vuex'

export default {
  props: {
    id: String,
    value: {
      type: Array,
      default: () => [],
    },
    image: String,
    actions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      layer: {
        handler: null,
        group: null,
      },
      selected: null,
      mouse: {
        x: 0,
        y: 0,
      },
      dropdownIsOpen: false,
      settingsIsOpen: false,
    }
  },
  computed: {
    ...mapState('Flatfinder', ['item']),
    draft() {
      return this.$path('item.project.draftId')
    },
    usedIds() {
      return this.value
        .map(v => v.reference.ref.id)
        .filter(v => !this.settingsIsOpen || v !== this.selected.item.reference.ref.id)
    },
  },
  methods: {
    resourceIsDisabled(item) {
      return [this.id, ...this.usedIds].includes(item.id)
    },
    getStyle(item) {
      return { className: `flatfinder-layer flatfinder-layer--status-${getStatusClass(item.reference)}` }
    },
    getTitle(item) {
      return getTitle({ ...item.reference.ref, onModel: item.reference.onModel })
    },

    /**
     * Triggered when finished drawing a new layer
     * @param {Object} layer
     */
    add(layer) {
      this.$refs.selector.$children[0].open()
      this.$refs.selector.$once('input', rawItem => {
        this.$refs.selector.$children[0].close()

        if (!rawItem) return
        let { onModel, ...ref } = rawItem

        const item = {
          reference: { ref, onModel },
          data: layer,
        }

        this.$emit('input', [...this.value, item])

        if (onModel === 'Link')
          this.$nextTick(() => {
            this.selected = { item, layer }
            this.settingsIsOpen = true
          })
      })
    },

    /**
     * Triggered when clicking on a layer,
     * and opens a context menu.
     *
     * @param {Object} layer
     */
    openActions(layer) {
      let match = this.value.find(v => v.reference.ref.id === layer._external_id)
      if (!match) return

      // if the function is triggered by hitting "Enter"
      const bounds = layer.event.originalEvent.target.getBoundingClientRect()
      const centerPoint = { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 }

      this.mouse.x = layer.event.originalEvent.clientX || centerPoint.x
      this.mouse.y = layer.event.originalEvent.clientY || centerPoint.y

      this.selected = { item: match, layer }

      this.dropdownIsOpen = true
    },
    getResources(filter) {
      return FlatfinderApi.get(
        {
          id: this.id,
          filter,
        },
        {
          query: 'FlatfinderItems',
          draft: this.draft,
        },
      ).then(Nest)
    },

    /**
     * Triggered when editing or deleting a single layer.
     * Moves the specified layer to a separate feature group.
     *
     * This is needed due to how the [Leaflet.draw](https://leaflet.github.io/Leaflet.draw/docs/leaflet-draw-latest.html) plugin implements editing.
     *
     * @param {Object} layer
     */
    createFeatureGroup(layer) {
      this.layer.group = new this.$refs.leaflet.Leaflet.featureGroup([layer])
      this.$refs.leaflet.map.addLayer(this.layer.group)
    },
    removeFeatureGroup() {
      this.layer.group = null
    },

    /**
     * Triggered when editing a single layer.
     */
    editLayer() {
      this.createFeatureGroup(this.selected.layer)

      this.layer.handler = new this.$refs.leaflet.Leaflet.EditToolbar.Edit(this.$refs.leaflet.map, {
        featureGroup: this.layer.group,
        selectedPathOptions: {
          dashArray: '10, 10',
          color: '#fe57a1',
          weight: 3,
          maintainColor: false,
        },
      })

      this.layer.handler.enable()
    },

    /**
     * Triggered when deleting a single layer.
     */
    deleteLayer() {
      this.createFeatureGroup(this.selected.layer)

      this.layer.handler = new this.$refs.leaflet.Leaflet.EditToolbar.Delete(this.$refs.leaflet.map, {
        featureGroup: this.layer.group,
      })

      this.layer.handler.enable()
      this.layer.handler._removeLayer(this.selected.layer)

      this.confirmEdit()
    },
    openSettings(value = true) {
      this.settingsIsOpen = value
    },
    cancelEdit() {
      if (!this.layer.handler) return

      this.layer.handler.revertLayers()
      this.layer.handler.disable()
      this.removeFeatureGroup()
      this.layer.handler = null
      this.selected = null
    },
    confirmEdit() {
      if (!this.layer.handler) return

      this.layer.handler.save()
      this.layer.handler.disable()
      this.removeFeatureGroup()
      this.layer.handler = null
      this.selected = null
    },
    onSettingsSubmit(item) {
      let match = this.value.find(v => v.reference.ref.id === this.selected.item.reference.ref.id)
      if (!match) return

      PartialUpdate(match, item)

      this.$emit('input', this.value)
      this.$refs.leaflet.render()
      this.selected = null
    },
  },
  components: {
    Leaflet,
    ResourceSelect,
    SettingsDropdown,
    SettingsModal,
    ButtonComponent,
  },
}
</script>

<style lang="scss">
$flatfinder-statuses: (
  sale: success,
  rent: success,
  sold: error,
  rented: error,
  reserved: warning,
  upcoming: info,
  draft: dark-grey,
  none: dark-grey,
);

.flatfinder {
  position: relative;

  &__actions {
    z-index: 500;
    position: absolute;
    right: 1rem;
    bottom: 1rem;

    padding: 0.5rem;
    border-radius: var(--border-radius-md);
    background-color: white;
    opacity: 1;
    translate: 0;

    display: flex;
    gap: 0.5rem;

    transform-origin: right;
  }

  .leaflet-draw-edit-edit {
    display: none;
  }

  .flatfinder-layer {
    fill-opacity: 0.25;

    @each $status, $value in $flatfinder-statuses {
      &--status-#{$status} {
        fill: var(--#{$value});
      }
    }
  }
}

.actions-enter,
.actions-leave-to {
  opacity: 0;
  scale: 0.95;
}

.actions-enter-active,
.actions-leave-active {
  transition: all 200ms ease;
}
</style>
